import type { ElementType } from "react"

import type { Product } from "@/components/marketplace/types"
import { Heading, Text } from "@/components/ui"
import { TileImage } from "@/components/ui/tile-image"
import { Link } from "@/i18n/navigation"

interface LookCardProps {
  title: string
  // The row's own caption — "Más vendidos" in the reference. Sits above the tiles.
  label?: string
  image: string
  imageAlt?: string
  href?: string
  tiles: Product[]
  priority?: boolean
  serveId?: string
}

// A themed door into a collection: one photograph, a headline on it, and the collection's
// bestsellers along the bottom. The tiles are real resolved products, which is what
// separates this from an editorial tile — the picture sells the idea and the packshots
// prove there is something to buy behind it.
//
// The whole card is the link, so the tiles are plain images rather than links of their own:
// an anchor inside an anchor is invalid, and the card already goes where they would.
//
// Three tiles by design, but the section asks the backend for four. `minItems` on a
// product-sourced section is 4, and the spare also covers a product going out of stock
// between resolve and render without leaving the row short.
export const LookCard = ({ title, label, image, imageAlt, href, tiles, priority, serveId }: LookCardProps) => {
  const Card: ElementType = href ? Link : "div"
  return (
    <Card
      {...(href ? { href } : {})}
      data-nelo-serve={serveId}
      className="press group focus-visible:ring-brand rounded-12x aspect-look bg-surface-newgray @container relative block min-w-0 overflow-hidden focus-visible:ring-2 focus-visible:ring-offset-4 focus-visible:outline-none"
    >
      <TileImage
        src={image}
        alt={imageAlt ?? ""}
        fill
        sizes="(max-width: 1024px) 90vw, 25vw"
        priority={priority}
        className="duration-slow ease-productive object-cover transition-transform motion-safe:group-hover:scale-105"
      />
      {/* Top-down, because the headline is at the top. Leaves the tiles on clear picture. */}
      <span aria-hidden className="bg-scrim-top absolute inset-0" />
      {/* Padding lives on this overlay, not the card, so the @container above measures the
          unpadded box and the headline's type step resolves against the real card width. */}
      <span className="absolute inset-0 flex flex-col justify-between p-500">
        {/* tone on the element: globals.css sets a base colour on p/a/span and a base rule
            beats inheritance, so an inverted wrapper would not cascade down to these. */}
        <Heading as="span" variant="lookTitle" family="hero" tone="inverted" className="block">
          {title}
        </Heading>
        <span className="flex flex-col gap-200">
          {label && (
            <Text as="span" variant="body" family="text" tone="inverted">
              {label}
            </Text>
          )}
          <span className="flex gap-150">
            {tiles.slice(0, 3).map((tile) => (
              <span
                key={tile.id}
                data-nelo-item={tile.id}
                data-nelo-serve={tile.serveId}
                className="rounded-8x bg-surface-paper relative aspect-square min-w-0 flex-1 overflow-hidden"
              >
                <TileImage src={tile.image} alt={tile.title} fill sizes="120px" className="object-contain p-150" />
              </span>
            ))}
          </span>
        </span>
      </span>
    </Card>
  )
}
